// Owned by mju-ai-workbench/shared/schemas/routineCatalogue.ts — the published routine, the catalogue's listing, the routes and the policy clause (D363). The AIOE session mirrors it to mju-aioe/packages/shared/src/schemas/routineCatalogue.ts; the workbench never mirrors it back. Keep the two identical.
/**
 * Routines shared through a project's catalogue (D363, JP 25 Oct 2026: "routines, like the micro apps, need to be available
 * from the organisational catalog"). OURS: the workbench defines these shapes; the team's hub implements `/routines/v1` from
 * them (desktop/src/main/teamHub/routines.ts), and the AIOE mirrors the file and builds the same routes at the organisation.
 * Apps are the model (shared/schemas/microapps.ts): the same states, the same review and withdraw rules, the same policy words.
 *
 * A published routine is a routine's definition with everything that belongs to one machine or one person taken out — no id,
 * no project, no repository, no team or specialist, no dates, no secret, no path, no person's name — and **parameters** in their
 * place, which the person who adds it fills ("Which repository does it run in?", "Which specialist answers it?"). Beside the
 * definition, a manifest a reader can judge it by: its name and purpose, what it runs (a prompt to an agent, work filed for a
 * team, or a tool on the machine), whether it runs on a timetable or when asked, what it reads and what it changes (the
 * Control API's scope words, as apps use them), its version, the hash of its canonical JSON, and its author by name.
 *
 * The routes at a project's home (the team's hub; the organisation for Enterprise). Base `/routines/v1`; `:projectId` is the
 * project (`prj_…`); the caller is a paired device or a signed-in person, as every hub route. A node's write names the member
 * it is for (the principal header), as for apps.
 *
 *   GET    /routines/v1/:projectId                             → {@link RoutineCatalogue}: the newest listed version of each routine,
 *                                                                the caller's own pending and rejected ones, and for an owner or
 *                                                                administrator everyone's pending ones. Filters as query
 *                                                                parameters ({@link routineCatalogueQuerySchema}: `q`, `runs`,
 *                                                                `schedule`, `status`), applied by {@link filterRoutineListings}.
 *   POST   /routines/v1/:projectId                             {@link RoutinePublishRequest} → 201 {@link RoutineListing}. The home
 *                                                                sets the author to the caller (by name, never the body's) and
 *                                                                recomputes the hash. 409 `version_exists` (a version is
 *                                                                immutable and must be newer than the newest), 409 `taken` (the id
 *                                                                is someone else's routine), 403 `publishing_off`,
 *                                                                `scope_exceeded`, `scope_excluded`, `commands_not_allowed`;
 *                                                                400 `invalid` with the problem. `status` is `listed` under
 *                                                                `publishing: "anyone"` and `pending` under `"review"`.
 *   GET    /routines/v1/:projectId/:routineId                  → { versions: {@link RoutineListing}[] }: every version the caller
 *                                                                may see, newest first.
 *   GET    /routines/v1/:projectId/:routineId/versions/:v      → {@link RoutineListing}: one version, with the definition to install.
 *   POST   /routines/v1/:projectId/:routineId/versions/:v/review  {@link RoutineReviewDecision} → {@link RoutineListing}. An owner or
 *                                                                administrator; never the author (403 `own_routine`); only a
 *                                                                pending version (409 `changed`); a rejection says why (400).
 *   POST   /routines/v1/:projectId/:routineId/withdraw         → { withdrawnAt }: the author (anyone who published a version of
 *                                                                it) or an owner or administrator. Every version is marked
 *                                                                withdrawn; it stops being offered and the copies people added
 *                                                                keep running where they are.
 *   POST   /routines/v1/:projectId/:routineId/versions/:v/install  { hash } → { installs }: counted once per person per version;
 *                                                                409 `changed` when the hash is not the listed one.
 *
 * Refusals are `{ error, message }` with the words in {@link ROUTINE_CATALOGUE_ERRORS}. A pending version is an Inbox record for
 * the project's owners: kind `decision`, needs-you, dedupe key {@link routineReviewDedupeKey}, actions Approve (the rpc
 * {@link ROUTINE_REVIEW_DECIDE_RPC}, `decision: "approve"`) and Review (`decision: "open"`, `keepOpen`); it settles when the
 * version is decided or withdrawn.
 *
 * Words for people: routine, catalogue, publish, add, review, withdraw, reads, changes, asks when you add it. Never "manifest",
 * "parameter" or "scope" in the interface.
 */
import { z } from "zod";
import { describeRoutineTriggerPolicy, routineTriggerPolicySchema } from "./routineTriggers.js";
export const ROUTINE_CATALOGUE_SCHEMA_VERSION = 1;
// ── What a routine runs, reads and changes ─────────────────────────────
/**
 * What a published routine runs, in three classes: `prompt` asks one agent for an answer (or writes a report from the records);
 * `plan` files or starts work for a team or the Scribe; `command` runs a tool on the machine with no agent in the loop. The
 * policy keeps `command` out of the catalogue unless it says otherwise.
 */
export const ROUTINE_RUN_CLASSES = ["prompt", "plan", "command"];
export const ROUTINE_RUN_WORDS = {
    prompt: "Asks an agent",
    plan: "Files work for a team",
    command: "Runs a tool on the machine",
};
export const ROUTINE_SCHEDULE_KINDS = ["on-demand", "timetable"];
export const ROUTINE_SCHEDULE_WORDS = { "on-demand": "When asked", timetable: "On a timetable" };
/** The Control API scopes a routine may need: what its runs read and change, said in the words apps use. */
export const ROUTINE_SCOPES = ["agents:read", "tasks:read", "memory:read", "prompts:write", "tasks:write", "teams:write", "agents:write", "memory:write"];
/** What no published routine is ever granted, whatever the policy says: approvals, settings, git, terminals, the window. */
export const ROUTINE_EXCLUDED_SCOPES = ["permissions:write", "settings:write", "git:write", "ui:view", "ui:control", "terminals:read", "terminals:write", "composer:write", "diagrams:write", "shell:read", "shell:write"];
/** A scope in the words a person reads ("Reads your tasks"; "Changes the task board"). */
export const ROUTINE_SCOPE_WORDS = {
    "agents:read": "your agents and runs",
    "tasks:read": "your tasks, plans and roadmap",
    "memory:read": "the project's memory",
    "prompts:write": "a message to an agent",
    "tasks:write": "the task board",
    "teams:write": "starts a team",
    "agents:write": "starts an agent's run",
    "memory:write": "the project's memory",
};
/** The kinds of action a published routine may carry: every kind a routine has (shared/protocol/shell.ts `RoutineAction`). */
export const PUBLISHED_ROUTINE_KINDS = [
    "facilitator-prompt",
    "start-team",
    "ops-review",
    "review",
    "memory-sync",
    "architecture-describe",
    "roadmap-refresh",
    "project-roadmap-refresh",
    "project-architecture-refresh",
    "status-report",
];
/** The kinds that belong to the whole project, and the kinds a project's routine may carry (the same rule the desktop keeps). */
export const PROJECT_ONLY_KINDS = ["project-roadmap-refresh", "project-architecture-refresh"];
export const PROJECT_KINDS = ["facilitator-prompt", "project-roadmap-refresh", "project-architecture-refresh", "status-report"];
// ── Parameters: what the person who adds it fills ──────────────────────
/** What the installer is asked: the repository it runs in, the specialist who answers it, the team that takes it. */
export const ROUTINE_PARAMETER_KINDS = ["repository", "specialist", "team"];
const paramName = z.string().regex(/^[a-z][a-z0-9-]{0,30}$/, "a parameter's name is lower-case letters, digits and hyphens");
export const routineParameterSchema = z
    .object({
    name: paramName,
    kind: z.enum(ROUTINE_PARAMETER_KINDS),
    /** The question, as the person reads it when they add the routine. */
    label: z.string().min(1).max(80),
    /** Without an answer the routine cannot be added. An optional one left empty takes the installer's default. */
    required: z.boolean(),
})
    .strict();
const paramRef = z.object({ param: paramName }).strict();
// ── The definition ─────────────────────────────────────────────────────
const review = z.enum(["auto", "human"]).optional();
/** A routine's action with every machine- and person-specific value replaced by a reference to a parameter. */
export const publishedRoutineActionSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("facilitator-prompt"), text: z.string().min(1).max(4000), runner: z.object({ kind: z.enum(["specialist", "team"]), param: paramName }).strict().optional() }).strict(),
    z.object({ kind: z.literal("start-team"), team: paramRef }).strict(),
    z.object({ kind: z.literal("ops-review"), team: paramRef.optional() }).strict(),
    z.object({ kind: z.literal("review"), runner: paramRef.optional() }).strict(),
    z.object({ kind: z.literal("memory-sync") }).strict(),
    z.object({ kind: z.literal("architecture-describe") }).strict(),
    z.object({ kind: z.literal("roadmap-refresh"), review }).strict(),
    z.object({ kind: z.literal("project-roadmap-refresh"), review }).strict(),
    z.object({ kind: z.literal("project-architecture-refresh"), review }).strict(),
    z
        .object({
        kind: z.literal("status-report"),
        scope: z.enum(["repository", "project"]).optional(),
        period: z.enum(["day", "week", "last-week", "month", "last-month"]).optional(),
        format: z.enum(["md", "okf"]).optional(),
        /** A repository template's file name, without folders. */
        template: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._ -]{0,80}$/).optional(),
    })
        .strict(),
]);
/** Five cron fields, each a number, a range, a list, a step or `*`: shape only; the desktop checks the values when it is added. */
const CRON_RE = /^\s*(\S+\s+){4}\S+\s*$/;
const CRON_FIELD_RE = /^(\*|\d{1,2}(-\d{1,2})?)(\/\d{1,2})?(,(\*|\d{1,2}(-\d{1,2})?)(\/\d{1,2})?)*$/;
export const publishedScheduleSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("on-demand") }).strict(),
    z
        .object({
        kind: z.literal("timetable"),
        /** Five-field cron, in the local time zone of the machine that runs it. */
        cron: z.string().max(100).refine((c) => CRON_RE.test(c) && c.trim().split(/\s+/).every((f) => CRON_FIELD_RE.test(f)), "a timetable is five cron fields"),
        /** Every second week and the like; it counts from the day it is added. Start and end dates are the installer's. */
        every: z.object({ count: z.number().int().min(1).max(52), unit: z.enum(["day", "week", "month"]) }).strict().optional(),
    })
        .strict(),
]);
export const routineDefinitionSchema = z
    .object({
    /** One repository's routine, or the whole project's (it works from the project's own roadmap, tasks and architecture). */
    scope: z.enum(["repository", "project"]),
    /** The repository it runs in: required for a repository's routine, optional ("runs from") for a project's. */
    repository: paramRef.optional(),
    action: publishedRoutineActionSchema,
    schedule: publishedScheduleSchema,
    /** Start the workbench for its repository when it is not running. */
    wake: z.boolean(),
})
    .strict();
// ── The manifest ───────────────────────────────────────────────────────
const routineId = z.string().regex(/^[a-z0-9][a-z0-9-]{0,40}$/, "a routine's id is lower-case letters, digits and hyphens");
const author = z.object({ id: z.string().min(1).max(200), name: z.string().min(1).max(120) }).strict();
export const routineManifestSchema = z
    .object({
    schema: z.literal(ROUTINE_CATALOGUE_SCHEMA_VERSION),
    id: routineId,
    name: z.string().min(1).max(80),
    purpose: z.string().min(1).max(200),
    /** `major.minor.patch`; a published version is immutable. */
    version: z.string().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}$/),
    author,
    /** What it runs ({@link routineNeeds}): checked against the definition. */
    runs: z.enum(ROUTINE_RUN_CLASSES),
    scheduleKind: z.enum(ROUTINE_SCHEDULE_KINDS),
    /** The scopes it reads and changes ({@link routineNeeds}): checked against the definition. */
    reads: z.array(z.string().max(40)).max(8),
    writes: z.array(z.string().max(40)).max(8),
    /** What the person who adds it is asked; every one the definition names, and none it does not. */
    parameters: z.array(routineParameterSchema).max(4),
    definition: routineDefinitionSchema,
    /** SHA-256 of {@link canonicalRoutineManifest}: the manifest without `hash`, `publishedAt` and `withdrawnAt`, keys sorted. */
    hash: z.string().regex(/^[0-9a-f]{64}$/).optional(),
    publishedAt: z.string().max(40).optional(),
    withdrawnAt: z.string().max(40).optional(),
})
    .strict();
/** The manifest with the fields a hash must not cover removed, keys sorted: what is hashed and what is compared. */
export function canonicalRoutineManifest(m) {
    const { hash: _h, publishedAt: _p, withdrawnAt: _w, ...rest } = m;
    const sort = (v) => {
        if (Array.isArray(v))
            return v.map(sort);
        if (v && typeof v === "object")
            return Object.fromEntries(Object.entries(v).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([k, x]) => [k, sort(x)]));
        return v;
    };
    return JSON.stringify(sort(rest));
}
/** A routine's id from its name: "Morning brief" → `morning-brief`. */
export function routineSlug(name) {
    const s = name
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 41)
        .replace(/-+$/, "");
    return s || "routine";
}
/** What a definition runs, reads and changes: the manifest's `runs`, `scheduleKind`, `reads` and `writes` are this. */
export function routineNeeds(d) {
    const a = d.action;
    const scheduleKind = d.schedule.kind;
    const sorted = (xs) => [...new Set(xs)].sort();
    switch (a.kind) {
        case "facilitator-prompt":
            return a.runner?.kind === "team"
                ? { runs: "plan", scheduleKind, reads: sorted(["agents:read", "tasks:read", "memory:read"]), writes: sorted(["prompts:write", "tasks:write"]) }
                : { runs: "prompt", scheduleKind, reads: sorted(["agents:read", "tasks:read", "memory:read"]), writes: ["prompts:write"] };
        case "review":
            return { runs: "prompt", scheduleKind, reads: sorted(["agents:read", "tasks:read", "memory:read"]), writes: ["agents:write"] };
        case "status-report":
            return { runs: "prompt", scheduleKind, reads: sorted(["agents:read", "tasks:read"]), writes: ["agents:write"] };
        case "start-team":
            return { runs: "plan", scheduleKind, reads: sorted(["agents:read", "tasks:read"]), writes: ["teams:write"] };
        case "ops-review":
            return { runs: "plan", scheduleKind, reads: sorted(["agents:read", "tasks:read"]), writes: sorted(["prompts:write", "tasks:write"]) };
        case "architecture-describe":
        case "roadmap-refresh":
        case "project-roadmap-refresh":
        case "project-architecture-refresh":
            return { runs: "plan", scheduleKind, reads: sorted(["tasks:read", "memory:read"]), writes: ["tasks:write"] };
        case "memory-sync":
            return { runs: "command", scheduleKind, reads: ["memory:read"], writes: ["memory:write"] };
    }
}
/** The parameters a definition names, in the order it names them. */
export function referencedParameters(d) {
    const out = [];
    const add = (r) => r && !out.includes(r.param) && out.push(r.param);
    add(d.repository);
    const a = d.action;
    if (a.kind === "facilitator-prompt")
        add(a.runner);
    if (a.kind === "start-team" || a.kind === "ops-review")
        add(a.team);
    if (a.kind === "review")
        add(a.runner);
    return out;
}
/** The kind of answer each reference needs. */
function referenceKinds(d) {
    const out = {};
    if (d.repository)
        out[d.repository.param] = "repository";
    const a = d.action;
    if (a.kind === "facilitator-prompt" && a.runner)
        out[a.runner.param] = a.runner.kind;
    if ((a.kind === "start-team" || a.kind === "ops-review") && a.team)
        out[a.team.param] = "team";
    if (a.kind === "review" && a.runner)
        out[a.runner.param] = "specialist";
    return out;
}
// ── What is never published ────────────────────────────────────────────
/**
 * What a routine's own words may not carry: a secret, a path on someone's machine, an email address or a person's name. These
 * are checked by the workbench before it publishes and by the home when it takes a version; sentences for a person, empty when
 * clean. `names` are the people the checker knows (the author, the team's members); a word of theirs in the text is refused.
 */
export function sensitiveTextProblems(text, names = []) {
    const out = [];
    const secret = /\b(?:gh[pousr]_[A-Za-z0-9]{20,}|github_pat_[A-Za-z0-9_]{20,}|sk-(?:ant-)?[A-Za-z0-9_-]{20,}|xox[abprs]-[A-Za-z0-9-]{10,}|AKIA[0-9A-Z]{16}|AIza[0-9A-Za-z_-]{30,}|eyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,})|-----BEGIN [A-Z ]*PRIVATE KEY-----|\b(?:password|passwd|secret|token|api[_-]?key)\s*[:=]\s*\S{6,}/i;
    if (secret.test(text))
        out.push(ROUTINE_CATALOGUE_ERRORS.secret);
    const path = /(?:^|[\s"'`(=])(?:[A-Za-z]:[\\/]|\\\\[^\\\s]+\\|~[\\/]|\/(?:home|Users|mnt|var|etc|opt|tmp|root|srv|usr|workspaces?|private)\/)/;
    if (path.test(text))
        out.push(ROUTINE_CATALOGUE_ERRORS.path);
    if (/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/.test(text))
        out.push(ROUTINE_CATALOGUE_ERRORS.email);
    // As the name is written (a capital and all): "Mark" is a person, "mark the task done" is not.
    const named = names
        .flatMap((n) => [n.trim(), ...n.trim().split(/\s+/)])
        .filter((n) => n.length >= 3)
        .find((n) => new RegExp(`(^|[^\\p{L}])${n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}($|[^\\p{L}])`, "u").test(text));
    if (named)
        out.push(ROUTINE_CATALOGUE_ERRORS.person.replace("a person.", `a person (${named}).`));
    return out;
}
/** Every piece of free text a manifest carries, for {@link sensitiveTextProblems}. */
export function routineTexts(m) {
    const a = m.definition.action;
    return [m.name, m.purpose, ...m.parameters.map((p) => p.label), ...(a.kind === "facilitator-prompt" ? [a.text] : []), ...(a.kind === "status-report" && a.template ? [a.template] : [])];
}
/**
 * Problems a manifest has on its own, whatever the policy: a kind that does not belong at its scope, a repository's routine
 * with no repository to ask for, a `runs`, `scheduleKind`, `reads` or `writes` that is not what the definition needs, a
 * parameter named but not declared (or declared and never named, or of the wrong kind), an excluded scope, and words that
 * carry a secret, a path or a person. Sentences for a person; empty when sound.
 */
export function routineManifestProblems(m, names = []) {
    const out = [];
    const d = m.definition;
    const kind = d.action.kind;
    if (d.scope === "repository" && PROJECT_ONLY_KINDS.includes(kind))
        out.push("refreshing the project's roadmap or architecture is a routine for the whole project");
    if (d.scope === "project" && !PROJECT_KINDS.includes(kind))
        out.push("a routine for the whole project sends a prompt, refreshes the project's roadmap or architecture, or writes a status report");
    if (d.scope === "repository" && !d.repository)
        out.push("a routine for one repository asks which repository when it is added");
    const needs = routineNeeds(d);
    if (m.runs !== needs.runs)
        out.push(`${ROUTINE_CATALOGUE_ERRORS.invalid} (it runs ${needs.runs}, not ${m.runs})`);
    if (m.scheduleKind !== needs.scheduleKind)
        out.push(`${ROUTINE_CATALOGUE_ERRORS.invalid} (its schedule is ${needs.scheduleKind})`);
    const same = (a, b) => a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);
    if (!same(m.reads, needs.reads))
        out.push(`${ROUTINE_CATALOGUE_ERRORS.invalid} (it reads ${needs.reads.join(", ")})`);
    if (!same(m.writes, needs.writes))
        out.push(`${ROUTINE_CATALOGUE_ERRORS.invalid} (it changes ${needs.writes.join(", ")})`);
    const excluded = new Set(ROUTINE_EXCLUDED_SCOPES);
    for (const s of [...m.reads, ...m.writes]) {
        if (excluded.has(s))
            out.push(`${ROUTINE_CATALOGUE_ERRORS.scope_excluded} (${s})`);
        else if (!ROUTINE_SCOPES.includes(s))
            out.push(`${ROUTINE_CATALOGUE_ERRORS.invalid} (the scope ${s})`);
    }
    const refs = referenceKinds(d);
    const declared = new Map(m.parameters.map((p) => [p.name, p]));
    if (declared.size !== m.parameters.length)
        out.push("two questions have the same name");
    for (const [name, k] of Object.entries(refs)) {
        const p = declared.get(name);
        if (!p)
            out.push(`it names "${name}" but does not ask for it`);
        else if (p.kind !== k)
            out.push(`"${name}" asks for a ${p.kind} where a ${k} is needed`);
    }
    for (const p of m.parameters)
        if (!(p.name in refs))
            out.push(`it asks for "${p.name}" and never uses the answer`);
    if (d.scope === "repository" && d.repository && declared.get(d.repository.param)?.required === false)
        out.push("a routine for one repository cannot run without one: the repository question must be answered");
    if (kind === "start-team" && d.action.kind === "start-team" && declared.get(d.action.team.param)?.required === false)
        out.push("starting a team needs the team: that question must be answered");
    for (const t of routineTexts(m))
        out.push(...sensitiveTextProblems(t, names));
    return [...new Set(out)];
}
/** Parse and check a manifest: its shape, then {@link routineManifestProblems}. */
export function parseRoutineManifest(raw, names = []) {
    const parsed = routineManifestSchema.safeParse(raw);
    if (!parsed.success)
        return { ok: false, errors: parsed.error.issues.slice(0, 8).map((i) => `${i.path.join(".") || "routine"}: ${i.message}`) };
    const problems = routineManifestProblems(parsed.data, names);
    return problems.length ? { ok: false, errors: problems.slice(0, 8) } : { ok: true, manifest: parsed.data };
}
// ── Refusals ───────────────────────────────────────────────────────────
/** A refusal's word, and the sentence a person is shown. */
export const ROUTINE_CATALOGUE_ERRORS = {
    publishing_off: "Your organisation does not let people publish routines.",
    scope_exceeded: "This routine reads or changes more than your organisation lets a shared routine have.",
    scope_excluded: "This routine asks for something a shared routine is never allowed to do.",
    commands_not_allowed: "Your organisation shares only routines that ask an agent or file work: not ones that run a tool on the machine.",
    version_exists: "That version is already published. Publish a newer one.",
    taken: "Someone else already shares a routine with that name. Give yours another name.",
    own_routine: "You cannot approve your own routine.",
    changed: "This routine changed since it was listed. Look at it again, then add it.",
    not_listed: "That version is not offered any more.",
    not_reviewer: "Only an owner of the project can review this.",
    invalid: "This routine does not say what it runs the way a shared routine must.",
    secret: "It carries something that looks like a password or a key. Take it out: whoever adds the routine signs in their own way.",
    path: "It names a folder on someone's computer. Say what to look at in words: the repository is asked for when it is added.",
    email: "It carries an email address. Name a role instead: the team is asked for when it is added.",
    person: "It names a person. Name a role instead: who answers it is asked for when it is added.",
};
// ── Storage and the catalogue ──────────────────────────────────────────
export const ROUTINE_LISTING_STATUSES = ["listed", "pending", "rejected", "withdrawn"];
export const ROUTINE_LISTING_STATUS_WORDS = { listed: "Listed", pending: "Waiting for review", rejected: "Not accepted", withdrawn: "Withdrawn" };
export const routinePublishRequestSchema = z.object({ manifest: routineManifestSchema }).strict();
export const routineReviewDecisionSchema = z.object({ decision: z.enum(["approve", "reject"]), reason: z.string().max(400).optional() }).strict();
export const routineInstallRequestSchema = z.object({ hash: z.string().regex(/^[0-9a-f]{64}$/).optional() }).strict();
/** The listing's filters, as query parameters. Any left out: no narrowing. */
export const routineCatalogueQuerySchema = z
    .object({
    /** Words that must all appear in the name, purpose or author. */
    q: z.string().max(200).optional(),
    runs: z.enum(ROUTINE_RUN_CLASSES).optional(),
    schedule: z.enum(ROUTINE_SCHEDULE_KINDS).optional(),
    status: z.enum(ROUTINE_LISTING_STATUSES).optional(),
})
    .strict();
/** The query as a URL search string (`?q=…&runs=…`), empty when nothing narrows. */
export function routineCatalogueSearch(q) {
    const params = new URLSearchParams();
    for (const k of ["q", "runs", "schedule", "status"]) {
        const v = q?.[k]?.trim();
        if (v)
            params.set(k, v);
    }
    const s = params.toString();
    return s ? `?${s}` : "";
}
/** The query from a URL's search parameters; anything unknown or malformed is dropped. */
export function routineCatalogueQueryOf(search) {
    const raw = {};
    for (const k of ["q", "runs", "schedule", "status"]) {
        const v = search.get(k);
        if (v)
            raw[k] = v;
    }
    const parsed = routineCatalogueQuerySchema.safeParse(raw);
    if (parsed.success)
        return parsed.data;
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
        const one = routineCatalogueQuerySchema.safeParse({ [k]: v });
        if (one.success)
            Object.assign(out, one.data);
    }
    return out;
}
/** The listings a query keeps (the home applies it after deciding what the caller may see). */
export function filterRoutineListings(listings, q) {
    const words = (q?.q ?? "").trim().toLowerCase().split(/\s+/).filter(Boolean);
    return listings.filter((l) => {
        if (q?.runs && l.manifest.runs !== q.runs)
            return false;
        if (q?.schedule && l.manifest.scheduleKind !== q.schedule)
            return false;
        if (q?.status && l.status !== q.status)
            return false;
        if (!words.length)
            return true;
        const hay = `${l.manifest.name}\n${l.manifest.purpose}\n${l.publishedBy.name}`.toLowerCase();
        return words.every((w) => hay.includes(w));
    });
}
/** The Inbox record's rpc an owner approves from, and its dedupe key. */
export const ROUTINE_REVIEW_DECIDE_RPC = "aioe.routines.review.decide";
export const routineReviewDedupeKey = (routineId, version) => `routine-review:${routineId}@${version}`;
// ── The policy clause ──────────────────────────────────────────────────
/**
 * The `routines` clause (workspace policy, `.mojoup/policy.json`, D363): how routines are shared through the team's or the
 * organisation's catalogue. Left out: anyone may publish to their team and it is listed at once; a shared routine may read and
 * change what any routine does except run a tool on the machine.
 */
export const routinesPolicySchema = z.object({
    publishing: z.enum(["anyone", "review", "off"]).optional().describe("What happens when a person publishes a routine to the team or organisation: anyone (listed at once), review (an owner or administrator approves each version first), off. Absent: anyone."),
    maxScopes: z.array(z.string()).optional().describe("The most a shared routine may read and change, as Control API scopes (agents:read, tasks:read, memory:read, prompts:write, tasks:write, teams:write, agents:write, memory:write). Absent: all of them. Approvals, settings, git, terminals and the window are never granted."),
    allowCommands: z.boolean().optional().describe("Whether a shared routine may run a tool on the machine with no agent in the loop (refresh the notebook). Absent: false: only routines that ask an agent or file work for a team are published and added."),
    /** D378 (shared/schemas/routineTriggers.ts): whether routines start when something happens, and how often. Applies to every routine, shared or not. */
    triggers: routineTriggerPolicySchema.optional().describe("Routines that start when something happens (a pipeline fails, an issue is labelled, a pull request asks for review or gets changes requested, a node raises an alert, or the organisation forwards an event): whether they may, and at most how often each."),
});
/** The clause with its defaults filled in; the ceiling never holds an excluded scope, whatever a file says. */
export function effectiveRoutinesPolicy(p) {
    const excluded = new Set(ROUTINE_EXCLUDED_SCOPES);
    return {
        publishing: p?.publishing ?? "anyone",
        maxScopes: (p?.maxScopes ?? ROUTINE_SCOPES).filter((s) => !excluded.has(s)),
        allowCommands: p?.allowCommands ?? false,
    };
}
/** Why a policy would not let this routine be shared or added, as a refusal word; undefined when it may. */
export function routinePolicyRefusal(policy, m) {
    if (m.runs === "command" && !policy.allowCommands)
        return { error: "commands_not_allowed" };
    const excluded = new Set(ROUTINE_EXCLUDED_SCOPES);
    const all = [...m.reads, ...m.writes];
    const bad = all.find((s) => excluded.has(s));
    if (bad)
        return { error: "scope_excluded", detail: bad };
    const beyond = all.find((s) => !policy.maxScopes.includes(s));
    if (beyond)
        return { error: "scope_exceeded", detail: beyond };
    return undefined;
}
/** The clause in a sentence for the Workspace policy card. */
export function describeRoutinesPolicy(p) {
    const e = effectiveRoutinesPolicy(p);
    const pub = e.publishing === "off" ? "publishing routines is off" : e.publishing === "review" ? "published routines wait for an owner's review" : "published routines are listed at once";
    const all = ROUTINE_SCOPES.every((s) => e.maxScopes.includes(s));
    return `Routines: ${pub}; ${e.allowCommands ? "a shared routine may run a tool on the machine" : "a shared routine may only ask an agent or file work"}${all ? "" : `; it may read and change up to ${e.maxScopes.join(", ") || "nothing"}`}${p?.triggers ? `; ${describeRoutineTriggerPolicy(p.triggers)}` : ""}`;
}
//# sourceMappingURL=routineCatalogue.js.map